import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { NotificationStatus, NotificationType } from '@dev-fast/types';
import { filter, finalize, interval, lastValueFrom, switchMap, takeWhile, tap } from 'rxjs';

import { NotificationsService } from '@app/core/notification-service';
import { UserService } from '@app/core/user-service';

import { LoadingService } from '../services/loading-service';

@Injectable({
  providedIn: 'root',
})
export class LoadResolver implements Resolve<any> {
  readonly #loadingService = inject(LoadingService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #userService = inject(UserService);

  readonly #user = toSignal(this.#userService.user$);

  #isLoading = false; // Флаг для блокировки повторных запросов
  #currentRequest: Promise<any> | null = null; // Храним текущий запрос

  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
    const path = route.routeConfig?.path;
    if (!path) {
      return true;
    }
    if (this.#currentRequest) {
      return this.#currentRequest;
    }

    if (this.#isLoading) {
      return;
    } // Игнорируем новые запросы, если уже загружается
    this.#isLoading = true;

    try {
      this.#loadingService.setLoadingProgress(10);

      const progressUpdater$ = this.#loadingService.loadingProgress$
        .pipe(
          filter(Boolean),
          switchMap((progress) =>
            interval(100).pipe(
              tap(() => {
                if (progress < 75) {
                  this.#loadingService.setLoadingProgress(progress + 15);
                }
              }),
              takeWhile(() => progress < 75, true),
            ),
          ),
        )
        .subscribe();

      // Конвертируем Observable в Promise
      this.#currentRequest = lastValueFrom(
        this.#loadingService
          .getFirstLoadDataByRoute({
            route,
            user: this.#user(),
          })
          .pipe(
            finalize(() => {
              progressUpdater$.unsubscribe();
              this.#loadingService.setLoadingProgress(100);
              this.#isLoading = false;
              this.#currentRequest = null; // Сбрасываем после завершения
            }),
          ),
      );

      const result = await this.#currentRequest;

      // Если результат null, сбрасываем currentRequest немедленно
      if (result === null) {
        this.#currentRequest = null;
      }

      return result;
    } catch (error) {
      this.#handleError(error as HttpErrorResponse);
      this.#currentRequest = null; // Сбрасываем при ошибке
      return null; // или throw error, если нужно прервать навигацию
    }
  }

  #handleError(error: HttpErrorResponse): void {
    this.#notificationsService.addNotification({
      id: Date.now(),
      type: error.error && error.error.type ? error.error.type : NotificationType.Error,
      icon: 'warning',
      message: error.error && error.error.message ? error.error.message : typeof error.error === 'string' ? error.error : 'Error',
      createDate: Date.now(),
      system: true,
      status: NotificationStatus.new,
    });
  }
}
